import React, { useState } from 'react';
import facebookIcon from '../assets/pngegg (69) 1.png';
import { useNavigate } from 'react-router-dom';
import authService from '../services/authService';
import advancedOAuthService from '../services/advancedOAuthService';
import UserTypeSelector from './UserTypeSelector';
import type { UserType } from './UserTypeSelector';

const LoginForm: React.FC = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [userType, setUserType] = useState<UserType | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showResendVerification, setShowResendVerification] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error and resend verification state when user starts typing
    if (error) {
      setError('');
      setShowResendVerification(false);
    }
  };

  const handleUserTypeSelect = (type: UserType) => {
    setUserType(type);
    if (error) {
      setError('');
      setShowResendVerification(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    
    if (!userType) {
      setError('Please select whether you are a Brand Owner or Agency');
      return;
    }
    
    setLoading(true);
    setError('');

    try {
      await authService.login(formData);
      
      // Navigate based on user type selection
      if (userType === 'agencies') {
        navigate('/research-landing');
      } else {
        navigate('/optimize');
      }
    } catch (err: any) {
      if (err.response?.data?.detail) {
        const errorDetail = err.response.data.detail;
        if (errorDetail.includes('verify your email')) {
          setError('Please verify your email address before logging in. Check your email for a verification link or request a new one.');
          setShowResendVerification(true);
        } else {
          setError(errorDetail);
          setShowResendVerification(false);
        }
      } else {
        setError(err.message || 'Login failed. Please try again.');
        setShowResendVerification(false);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    if (!userType) {
      setError('Please select whether you are a Brand Owner or Agency');
      return;
    }
    
    try {
      setLoading(true);
      setError('');
      await advancedOAuthService.initiateOAuthLogin('google', true, userType); // Use popup with fallback
      
      // Navigate based on user type selection
      if (userType === 'agencies') {
        navigate('/research-landing');
      } else {
        navigate('/optimize');
      }
    } catch (error: any) {
      setError(advancedOAuthService.handleOAuthError(error));
    } finally {
      setLoading(false);
    }
  };
  
  const handleFacebookSignIn = async () => {
    try {
      setLoading(true);
      setError('');
      // Facebook OAuth functionality removed
      setError('Facebook sign-in is temporarily unavailable');
    } catch (error: any) {
      setError('Facebook sign-in failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleResendVerification = async () => {
    try {
      setLoading(true);
      setError('');
      
      if (!formData.email) {
        setError('Please enter your email address first');
        return;
      }

      await authService.forgotPassword(formData.email);
      setError('Verification email sent! Please check your inbox.');
      setShowResendVerification(false);
    } catch (err: any) {
      setError(err.message || 'Failed to resend verification email');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="w-full space-y-6">
      {/* User Type Selection */}
      <UserTypeSelector
        selectedType={userType}
        onSelectType={handleUserTypeSelect}
        disabled={loading}
      />

      {/* Email Field */}
      <div>
        <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
          Email Address
        </label>
        <input
          type="email"
          id="email"
          name="email"
          value={formData.email}
          onChange={handleInputChange}
          required
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          placeholder="Enter your email"
        />
      </div>

      {/* Password Field */}
      <div>
        <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
          Password
        </label>
        <div className="relative">
          <input
            type={showPassword ? 'text' : 'password'}
            id="password"
            name="password"
            value={formData.password}
            onChange={handleInputChange}
            required
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent pr-12"
            placeholder="Enter your password"
          />
          <button
            type="button"
            onClick={togglePasswordVisibility}
            className="absolute inset-y-0 right-0 pr-3 flex items-center"
          >
            {showPassword ? (
              <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
              </svg>
            ) : (
              <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
            )}
          </button>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          <p className="text-sm">{error}</p>
          {showResendVerification && (
            <button
              type="button"
              onClick={handleResendVerification}
              className="mt-2 text-sm text-red-600 hover:text-red-800 underline"
            >
              Resend verification email
            </button>
          )}
        </div>
      )}

      {/* Submit Button */}
      <button
        type="submit"
        disabled={loading || !userType}
        className={`w-full py-3 px-4 rounded-lg font-semibold text-white transition-colors ${
          loading || !userType
            ? 'bg-gray-400 cursor-not-allowed'
            : 'bg-purple-600 hover:bg-purple-700'
        }`}
      >
        {loading ? 'Signing in...' : 'Sign In'}
      </button>

      {/* Divider */}
      <div className="relative">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-gray-300" />
        </div>
        <div className="relative flex justify-center text-sm">
          <span className="px-2 bg-white text-gray-500">Or continue with</span>
        </div>
      </div>

      {/* Social Login Buttons */}
      <div className="grid grid-cols-2 gap-3">
        <button
          type="button"
          onClick={handleGoogleSignIn}
          disabled={loading || !userType}
          className={`flex items-center justify-center px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors ${
            loading || !userType ? 'opacity-50 cursor-not-allowed' : ''
          }`}
        >
          <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
          Google
        </button>
        <button
          type="button"
          onClick={handleFacebookSignIn}
          disabled={loading || !userType}
          className={`flex items-center justify-center px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors ${
            loading || !userType ? 'opacity-50 cursor-not-allowed' : ''
          }`}
        >
          <img src={facebookIcon} alt="Facebook" className="w-5 h-5 mr-2" />
          Facebook
        </button>
      </div>
    </form>
  );
};

export default LoginForm; 